import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, User, Phone, MapPin, CheckCircle2, Loader2, AlertCircle, ChevronRight, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { contactInfo } from '../data/services';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingModal = ({ isOpen, onClose, serviceId, serviceName }) => {
  const [step, setStep] = useState(1); // 1: Select Slot, 2: Details, 3: Success
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    user_name: '',
    user_phone: '',
    user_location: ''
  });

  useEffect(() => {
    if (isOpen && serviceId) {
      fetchSlots();
      setStep(1);
      setSelectedSlot(null);
      setError(null);
      setFormData({ user_name: '', user_phone: '', user_location: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, serviceId]);

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/slots?service_id=${serviceId}`);
      setSlots(response.data);
    } catch (err) {
      setError('Failed to load available slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setBookingLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/bookings`, {
        slot_id: selectedSlot.id,
        user_name: formData.user_name,
        user_phone: formData.user_phone,
        service_id: serviceId,
        user_location: formData.user_location
      });
      // Auto-open WhatsApp with booking details
      const slotDate = new Date(selectedSlot.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      const msg = `Hi, I have booked *${serviceName}* on *${slotDate}* at *${selectedSlot.time}*. My name is *${formData.user_name}*, phone: *${formData.user_phone}*${formData.user_location ? `, location: *${formData.user_location}*` : ''}. Please confirm my booking.`;
      window.open(`https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(msg)}`, '_blank');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Booking failed. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  // Build WhatsApp message with slot details
  const getWhatsAppLink = () => {
    const slotDate = selectedSlot ? new Date(selectedSlot.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
    const slotTime = selectedSlot?.time || '';
    const msg = `Hi, I have booked *${serviceName}* on *${slotDate}* at *${slotTime}*. My name is *${formData.user_name}* and my phone is *${formData.user_phone}*. Please confirm my booking.`;
    return `https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(msg)}`;
  };

  // Group slots by date
  const groupedSlots = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-zinc-50/50">
            <div>
              <h3 className="text-xl font-bold text-slate-800 font-['Oswald']">Book a Slot</h3>
              <div className="flex items-center gap-2">
                <p className="text-xs text-blue-500 font-semibold">{serviceName}</p>
                <span className="text-[10px] text-slate-400 font-medium">| No online Payments are available</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-200/50 rounded-full transition-colors"
            >
              <X size={20} className="text-slate-400" />
            </button>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-slate-800 font-semibold mb-2">
                  <Calendar size={18} className="text-blue-500" />
                  Select Available Date & Time
                </div>

                {loading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-3">
                    <Loader2 className="animate-spin text-blue-500" size={32} />
                    <p className="text-slate-500 text-sm">Finding availabilities...</p>
                  </div>
                ) : Object.keys(groupedSlots).length > 0 ? (
                  <div className="max-h-[350px] overflow-y-auto space-y-4 pr-2 custom-scrollbar">
                    {Object.entries(groupedSlots).sort().map(([date, daySlots]) => (
                      <div key={date} className="space-y-2">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider sticky top-0 bg-white py-1">
                          {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                          {daySlots.sort((a,b) => a.time.localeCompare(b.time)).map((slot) => (
                            <button
                              key={slot.id}
                              onClick={() => setSelectedSlot(slot)}
                              className={`p-2 rounded-xl text-sm font-medium transition-all border ${
                                selectedSlot?.id === slot.id
                                  ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                                  : 'bg-zinc-50 border-slate-100 text-slate-600 hover:border-blue-300'
                              }`}
                            >
                              {slot.time}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-slate-500">
                    <Calendar size={40} className="mx-auto mb-3 opacity-20" />
                    <p>No available slots for this service.</p>
                    <p className="text-xs mt-1">Please contact us directly via WhatsApp or Call.</p>
                  </div>
                )}

                <button
                  disabled={!selectedSlot}
                  onClick={() => setStep(2)}
                  className="w-full mt-6 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-200 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all"
                >
                  Continue to Details
                  <ChevronRight size={18} />
                </button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleBooking} className="space-y-5">
                <div className="bg-blue-50 p-4 rounded-2xl flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-blue-500 shadow-sm">
                      <Clock size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-blue-600 font-medium">Selected Time</p>
                      <p className="text-sm font-bold text-slate-800">
                        {new Date(selectedSlot?.date).toLocaleDateString()} at {selectedSlot?.time}
                      </p>
                    </div>
                  </div>
                  <button 
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-xs font-bold text-blue-600 underline"
                  >
                    Change
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Your Name</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        required
                        type="text"
                        value={formData.user_name}
                        onChange={(e) => setFormData({...formData, user_name: e.target.value})}
                        className="w-full pl-12 pr-4 py-3 bg-zinc-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        required
                        type="tel"
                        value={formData.user_phone}
                        onChange={(e) => setFormData({...formData, user_phone: e.target.value})}
                        className="w-full pl-12 pr-4 py-3 bg-zinc-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="789xxxxxxx"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Your Location / Area</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="text"
                        value={formData.user_location}
                        onChange={(e) => setFormData({...formData, user_location: e.target.value})}
                        className="w-full pl-12 pr-4 py-3 bg-zinc-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="e.g. Tirupati, Andhra Pradesh"
                      />
                    </div>
                  </div>
                </div>

                <button
                  disabled={bookingLoading}
                  type="submit"
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-slate-200 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all mt-4"
                >
                  {bookingLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Processing...
                    </>
                  ) : (
                    <>
                      Confirm Booking
                      <CheckCircle2 size={18} />
                    </>
                  )}
                </button>
              </form>
            )}

            {step === 3 && (
              <div className="py-8 text-center space-y-6">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <CheckCircle2 size={40} />
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-slate-800 mb-2">Booking Confirmed!</h4>
                  <p className="text-slate-500">
                    Thank you {formData.user_name}. Your slot for <span className="font-bold text-blue-500">{serviceName}</span> is confirmed.
                  </p>
                  <p className="text-slate-500 text-sm mt-2">
                    📅 {new Date(selectedSlot?.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} at {selectedSlot?.time}
                  </p>
                  <p className="text-green-600 text-sm mt-3 font-semibold">
                    ✅ A WhatsApp message with your booking details has been sent.
                  </p>
                  <p className="text-slate-400 text-xs mt-2">
                    Our team will contact you shortly at {formData.user_phone}.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="w-full bg-slate-800 hover:bg-slate-900 text-white py-4 rounded-2xl font-bold transition-all"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
